/**
 * Bulk check-in body: collapsible Settings + File(s) list with expandable rows.
 *
 * Single-active model: Settings OR one file row is "active" at a time and bound to the shared
 * Redux check-in slice via `DocumentBasicItem`. Each section persists its own draft locally.
 *
 * Behavior:
 *  1) Settings Class/Format and view-copy auto-generate propagate to every file;
 *     changing Settings overrides any per-file Class/Format or auto-generate chosen earlier.
 *  2) Editing Class/Format or view-copy auto-generate inside a file row updates only that file
 *     (it becomes "overridden") until Settings changes those fields again.
 *  3) View-copy PDF uploads in a file row apply only to that file (stored per draft).
 *  4) Settings object connections replace connections on files that follow Settings; on files
 *     with their own selections, newly added Settings connections are merged in (not replaced).
 *  5) Settings "Delete OneDrive original" propagates to every file unless that file was
 *     individually overridden (`oneDriveDeleteOverridden`).
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  DefaultButton,
  Icon,
  PrimaryButton,
  Stack,
  Text,
} from "@fluentui/react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Typography,
  Tooltip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import { TaskListSquareSettingsRegular } from "@fluentui/react-icons";
import { useLocation, useNavigate } from "react-router-dom";

import DocumentBasicItem from "../../components/checkinDocuments/DocumentBasicItem";
import DeleteOneDriveCheckbox from "../check-in-new-revision/components/DeleteOneDriveCheckbox";
import ReusableDialog from "../../components/popupModals/ReusableDialog";
import MessageBoxContent from "../../components/popupModals/MessageBoxContent";
import { APP_ROUTES } from "../../routes/routeConfig";
import { useTheme } from "../../theme/OAS365Theme";
import { useCommonControlsStyle } from "../../theme/styles/CommonControlsStyle";
import { logger } from "../../utils/logger";
import { useAppDispatch } from "../../redux/hooks";
import {
  ON_ERROR_MESSAGE_INVOKE,
  ON_SUCCESS_MESSAGE_INVOKE,
} from "../../redux/constants/actionTypes";
import { ERROR_TYPE, SUCCESS_TYPE } from "../../redux/constants/messageTypes";
import { useCheckin } from "../../redux/hooks/useCheckin";
import { useCheckinDocument } from "../../redux/hooks/useCheckinDocument";
import { useMetadata } from "../../redux/hooks/useMetadata";
import { useTools } from "../../redux/hooks/useTools";
import { useLogin } from "../../redux/hooks/useLogin";
import { useOneDrive } from "../../redux/hooks/useOneDrive";
import { checkinDocumentsBulkThunk } from "../../redux/slices/checkinApi";
import {
  buildBulkDocInfo,
  bulkFormatMatches,
  clearBulkCheckinState,
  cloneViewCopyFiles,
  draftHasManualViewCopyAttachment,
  buildBulkOneDriveServerContext,
  collectBulkOneDriveDeleteIds,
  collectBulkMetadataUpdates,
  extractBulkObjectConnections,
  formatBulkCheckinToastMessage,
  getBulkBrowseIFSInfo,
  getBulkCheckinToastTitle,
  getBulkOneDriveFileUrlFromDoc,
  isViewCopyRequirementMet,
  getEffectiveViewCopyUploadCount,
  isWordLikeFileName,
  mergeBulkAutopopulatedDraft,
  parseBulkClassRules,
  resolveBulkViewRequiredFlag,
  mergeActiveFileViewCopyIntoDraftMap,
  mergeFileViewCopyIntoDraft,
  resolveOneDriveDeleteRequired,
} from "./checkInBulk.utils";
import { resolveGroupId } from "../check-in-new-list/checkInNewList.utils";
import {
  getDocumentDetails,
  getDocumentNewRevisionInfo,
  updateOneDriveMetadata,
} from "../../redux/api/documentsAPI";
import { normalizeDocumentRevisionInfoResponse } from "../../utils/normalizeDocumentRevisionInfoResponse";
import { guardObjectConnectionsAgainstConfigOverwrite } from "../../redux/hooks/useIFSBrowseAutopopulation";
import UserAuth from "../../auth/userAuth";
import {
  DATA_LOAD_STATUS,
  DOC_CHECK_IN_TYPE,
  DOCUMENT_LOAD_STATUS,
  REFRESH_TOKEN_ACTION,
} from "../../redux/constants/metaData";
import {
  TAB_KEY,
  CHECKIN_LOADING_MESSAGE,
} from "../check-in-new/components/checkInNew.constants";
import {
  isCheckinInLoadingState,
  shouldTriggerRefreshTokenPreAction,
} from "../check-in-new/components/checkInNew.helpers";
import { getFileName, getDocClassCodeOnly } from "../../functions/helperMethod";

/** Dev: surface `DefaultButton` binding (footer uses `PrimaryButton` only) — avoids ReferenceError if HMR serves a mixed chunk. */
void DefaultButton;

/** @typedef {{ key?: string, name?: string, title?: string, iconName?: string, downloadlink?: string, fileType?: string }} ListDocRow */
/** @typedef {{ docClass: string, docFormat: string, docTitle: string, overridden: boolean, viewRequired?: string, formatRequired?: string, objConRequired?: string, createViewCopy?: boolean, docReleaseStatus?: string, viewCopyOverridden?: boolean, releaseOverridden?: boolean, oneDriveDeleteRequired?: boolean, oneDriveDeleteOverridden?: boolean, objectConnections?: Array<any>, objectConnectionsOverridden?: boolean, viewCopyFiles?: Array<any>, uploadViewCopyCount?: number }} FileDraft */

const SETTINGS_KEY = "settings";
/** No file or settings section is actively mirrored into Redux. */
const INACTIVE_KEY = "inactive";

const BULK_ACC_HEADER_SUMMARY_SX = {
  minHeight: "56px !important",
  "&.Mui-expanded": { minHeight: "56px !important" },
  "& .MuiAccordionSummary-content": {
    alignItems: "center",
    margin: "12px 0 !important",
  },
};

const BULK_ACC_CARD_SURFACE_BG = "#f4fbf6";

const BULK_OC_DEBUG = false; // toggle to trace object-connection removal

const isApiFlagYes = (value) => value === true || value === "Y";
const normalizeObjectConnections = (items) =>
  (Array.isArray(items) ? items : [])
    .map((item) => ({
      luName: item?.luName ?? "",
      keyRef: item?.keyRef ?? "",
    }))
    .sort((a, b) =>
      `${a.luName}|${a.keyRef}`.localeCompare(`${b.luName}|${b.keyRef}`),
    );
const areObjectConnectionsEqual = (a, b) =>
  JSON.stringify(normalizeObjectConnections(a)) ===
  JSON.stringify(normalizeObjectConnections(b));

/**
 * Fetch class rules/formats for a browsed IFS class, retrying when the initial
 * page-entry call returns no formats (parallel autopopulation can race the API).
 * Stops early when formats resolve or no format size exists on the document.
 * @param {(className: string) => Promise<any>} onGetFormatsByClass
 * @param {string} docClass
 * @param {string} formatSize
 * @param {() => boolean} isCancelled
 */
const fetchBulkClassRulesWithRetry = async (
  onGetFormatsByClass,
  docClass,
  formatSize,
  isCancelled,
) => {
  let classRules = {
    formats: [],
    viewRequired: "N",
    formatRequired: "N",
    objConRequired: "N",
    restrictedAccess: "N",
  };
  const maxAttempts = 3;
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    try {
      const payload = await onGetFormatsByClass(docClass);
      if (payload) {
        classRules = parseBulkClassRules(payload);
      }
    } catch (err) {
      logger.warn("Failed to load formats for bulk browse file", err, "CHECKIN");
    }
    if (isCancelled()) break;
    if (classRules.formats.length > 0 || !formatSize) break;
    if (attempt < maxAttempts - 1 && !isCancelled()) {
      await new Promise((resolve) => setTimeout(resolve, 400 * (attempt + 1)));
    }
  }
  return classRules;
};

const getObjectConnectionKey = (item) =>
  `${item?.luName ?? ""}|${item?.keyRef ?? ""}`;

/** Connections present in `current` but not in `previous` (Settings delta). */
const getAddedObjectConnections = (previous, current) => {
  const currentList = Array.isArray(current) ? current : [];
  const previousKeys = new Set(
    normalizeObjectConnections(previous).map(getObjectConnectionKey),
  );
  return currentList.filter(
    (item) => !previousKeys.has(getObjectConnectionKey(item)),
  );
};

/** Union object connections by luName/keyRef, preserving full item fields. */
const unionObjectConnections = (base, additions) => {
  const seen = new Set();
  const merged = [];
  const baseList = Array.isArray(base) ? base : [];
  const additionList = Array.isArray(additions) ? additions : [];
  for (const item of [...baseList, ...additionList]) {
    const key = getObjectConnectionKey(item);
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    merged.push(item);
  }
  return merged;
};

const removeObjectConnectionsByKeys = (connections, keysToRemove) => {
  const list = Array.isArray(connections) ? connections : [];
  if (!(keysToRemove instanceof Set) || keysToRemove.size === 0) {
    return list;
  }
  return list.filter((item) => !keysToRemove.has(getObjectConnectionKey(item)));
};

const areViewCopyFilesEqual = (a, b) => {
  const arrA = Array.isArray(a) ? a : [];
  const arrB = Array.isArray(b) ? b : [];
  if (arrA.length !== arrB.length) {
    return false;
  }
  return arrA.every(
    (file, index) =>
      (file?.name ?? "") === (arrB[index]?.name ?? "") &&
      (file?.base64str?.length ?? 0) === (arrB[index]?.base64str?.length ?? 0),
  );
};

function TooltipWarningIcon({ incomplete }) {
  if (!incomplete) {
    return null;
  }
  const tip = "Complete all required fields";
  return (
    <Tooltip title={tip} arrow placement="top">
      <span style={{ display: "inline-flex" }}>
        <Icon
          iconName="Warning"
          ariaLabel={tip}
          styles={{
            root: {
              flexShrink: 0,
              color: "#eab308",
              fontSize: 18,
              height: 18,
              width: 18,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            },
          }}
        />
      </span>
    </Tooltip>
  );
}

function BulkCheckInFileRow({
  doc,
  expanded,
  incomplete,
  isAutopopulating = false,
  onToggleExpand,
  onTreeDataLoadingChange,
  objectConnectionTabKey,
  onObjectConnectionTabKeyChange,
  tabKey,
  oneDriveDeleteChecked,
  onOneDriveDeleteChange,
  onRemove,
}) {
  const theme = useTheme();
  const docLabel = doc.name ?? doc.title ?? doc.key ?? "Document";

  // console.log("docLabel:", docLabel, "incomplete:", incomplete);

  return (
    <Accordion
      expanded={expanded}
      onChange={(_e, exp) => onToggleExpand(doc.key, exp)}
      disableGutters
      sx={{
        mb: 1.25,
        boxShadow: "none !important",
        borderRadius: "8px",
        border: `1px solid ${theme.colors.addovation20Green ?? "#b7d9c8"}`,
        "&:before": { display: "none" },
        backgroundColor: BULK_ACC_CARD_SURFACE_BG,
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: "#4c8d6a" }} />}
        aria-controls={`bulk-file-${doc.key}-content`}
        id={`bulk-file-${doc.key}-header`}
        sx={{
          ...BULK_ACC_HEADER_SUMMARY_SX,
          px: 1,
          "& .MuiAccordionSummary-content": {
            ...BULK_ACC_HEADER_SUMMARY_SX["& .MuiAccordionSummary-content"],
            gap: "12px",
            minWidth: 0,
            overflow: "hidden",
          },
          "& .MuiAccordionSummary-expandIconWrapper": {
            marginLeft: "2px",
            display: "flex",
            alignItems: "center",
          },
        }}
      >
        <img
          src={doc.iconName}
          alt=""
          width={18}
          height={18}
          style={{ flexShrink: 0 }}
          onError={(e) => {
            e.target.style.visibility = "hidden";
          }}
        />
        <Typography
          sx={{
            flex: 1,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            color: "#2d6e4f",
            fontFamily: theme.fonts.regular,
            fontWeight: 600,
            fontSize: `${theme.fontSizes?.normal ?? 12}px`,
          }}
          title={docLabel}
        >
          {docLabel}
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "2px",
            flexShrink: 0,
          }}
        >
          <TooltipWarningIcon incomplete={incomplete} />
          <Tooltip title="Remove document" arrow placement="top">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onRemove?.(doc.key);
              }}
              sx={{
                padding: 0,
                width: 22,
                height: 22,
                color: "#d32f2f",
                flexShrink: 0,
                "&:hover": { backgroundColor: "rgba(211, 47, 47, 0.08)" },
              }}
            >
              <CloseIcon sx={{ fontSize: 22 }} />
            </IconButton>
          </Tooltip>
        </Box>
        <Box
          component="span"
          sx={{
            color: "#bdbdbd",
            fontSize: "12px",
            lineHeight: 1,
            flexShrink: 0,
          }}
        >
          |
        </Box>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          pt: 0,
          pb: 2,
          px: 2,
          backgroundColor: theme.colors.backgroundColor,
          display: "flex",
          flexDirection: "column",
          gap: "12px",
        }}
      >
        {expanded && (
          <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
            <DocumentBasicItem
              key={`file:${doc.key}`}
              omitOneDriveDeletionRow
              onTreeDataLoadingChange={onTreeDataLoadingChange}
              objectConnectionTabKey={objectConnectionTabKey}
              onObjectConnectionTabKeyChange={onObjectConnectionTabKeyChange}
              tabKey={tabKey}
              isAutopopulating={isAutopopulating}
            />
            {doc.source !== "browse" && (
              <DeleteOneDriveCheckbox
                checked={oneDriveDeleteChecked}
                onChange={onOneDriveDeleteChange}
              />
            )}
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
}

export default function CheckInNewBulkContent() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const styleTheme = useCommonControlsStyle();
  const tabKey = TAB_KEY;

  /** @type {{ documents?: ListDocRow[], from?: string }} */
  const state = location.state ?? {};

  /** Track removed document keys */
  const [removedDocumentKeys, setRemovedDocumentKeys] = useState(new Set());

  const documents = useMemo(() => {
    const docs = state.documents;
    const allDocs = Array.isArray(docs) ? docs : [];
    return allDocs.filter((doc) => !removedDocumentKeys.has(String(doc.key)));
  }, [state.documents, removedDocumentKeys]);

  /** `'settings'` | `'file:<key>'` — which section is mirrored into Redux. */
  const [activeKey, setActiveKey] = useState(SETTINGS_KEY);
  /** Visual expand/collapse for Settings; independent of `activeKey` so collapse does not unmount. */
  const [settingsPanelExpanded, setSettingsPanelExpanded] = useState(true);
  const [objectConnectionTabKey, setObjectConnectionTabKey] = useState(
    tabKey.checkinNewSelectedObj,
  );
  const [, setIsTreeDataLoading] = useState(false);

  /** Settings has no Title; each file has its own Title (and an `overridden` flag). */
  const [settingsDraft, setSettingsDraft] = useState({
    docClass: "",
    docFormat: "",
    viewRequired: "N",
    formatRequired: "N",
    objConRequired: "N",
    createViewCopy: false,
    docReleaseStatus: "",
    oneDriveDeleteRequired: true,
    objectConnections: [],
  });
  /** @type {[Record<string, FileDraft>, Function]} */
  const [draftByKey, setDraftByKey] = useState({});
  /** File keys whose IFS browse auto-population is currently loading (class combo spinner). */
  const [bulkAutopopulatingKeys, setBulkAutopopulatingKeys] = useState(
    () => new Set(),
  );

  const {
    docTitle,
    docClass,
    docFormat,
    formatRequired,
    viewRequired,
    uploadFiles,
    uploadViewCopyCount,
    createViewCopy,
    objConRequired,
    selectedObjectConnections,
    docReleaseStatus,
    viewCopyFiles,
    docClassFormatList,
    checkinDocumentProgress,
    onGetFormatsByClass,
    onObjectConnectionsLoad,
    getRecentObjects,
  } = useCheckin();
  const {
    objectConnectionDataLoad,
    docCheckinType,
    tokenRefreshPreActionInvoke,
  } = useMetadata();
  const {
    onFileUploadChange,
    onFileDataChange,
    onDocClassFormatChange,
    onDocTitleChange,
    onViewCopyUploadChange,
    onDocReleaseChange,
    onCreateViewCopyDisable,
    onCreateViewCopySelect,
    onObjectConnectionDataLoad,
    onDocClassFormatListChange,
    onRecentObjectConnectionChanged,
    onSelectedObjectConnectionChanged,
    onSelectedKeyLuNameChange,
    onRecentObjectSelectedDescription,
    onRecentObjectDataLoadStatusChanged,
    onAllowSelectionChange,
    onCheckinDocumentProgress,
    onOneDriveDataLoad,
    onDocCheckinTypeChange,
  } = useTools();
  const { teamsContext } = useLogin();
  const { deleteOneDriveDocumentsBulk } = useOneDrive();
  const { checkinBulkLoading } = useCheckinDocument();

  // Refs let callbacks read latest state without becoming new each render.
  const activeKeyRef = useRef(activeKey);
  const settingsDraftRef = useRef(settingsDraft);
  const draftByKeyRef = useRef(draftByKey);
  const documentsRef = useRef(documents);
  useEffect(() => {
    activeKeyRef.current = activeKey;
  }, [activeKey]);
  useEffect(() => {
    settingsDraftRef.current = settingsDraft;
  }, [settingsDraft]);
  useEffect(() => {
    draftByKeyRef.current = draftByKey;
  }, [draftByKey]);
  useEffect(() => {
    documentsRef.current = documents;
  }, [documents]);

  const handleRemoveDocument = useCallback((docKey) => {
    setRemovedDocumentKeys((prev) => new Set([...prev, String(docKey)]));
    setDraftByKey((prev) => {
      const next = { ...prev };
      delete next[String(docKey)];
      return next;
    });
  }, []);

  const isBrowseOnly = useMemo(
    () => documents.length > 0 && documents.every((d) => d.source === "browse"),
    [documents],
  );

  /** Guards for the Redux ↔ draft sync loop. */
  const lastSeenReduxRef = useRef({
    docClass: docClass ?? "",
    docFormat: docFormat ?? "",
    docTitle: typeof docTitle === "string" ? docTitle : "",
    createViewCopy: createViewCopy === true,
    docReleaseStatus: docReleaseStatus ?? "",
  });
  /** When we hydrate Redux from a draft, the next Redux update should NOT be saved back. */
  const expectedHydrationRef = useRef(null);
  const lastSeenObjectConnectionsRef = useRef(
    Array.isArray(selectedObjectConnections) ? selectedObjectConnections : [],
  );
  const expectedObjectConnectionsRef = useRef(null);
  const previousSettingsObjectConnectionsRef = useRef(
    settingsDraft.objectConnections ?? [],
  );
  const lastSeenViewCopyRef = useRef({
    files: cloneViewCopyFiles(viewCopyFiles),
    count: uploadViewCopyCount ?? 0,
  });
  const expectedViewCopyHydrationRef = useRef(null);
  const prevActiveKeyForHydrationRef = useRef(activeKey);

  useEffect(() => {
    if (documents.length) {
      logger.info(
        "[CheckInNewBulkContent] Loaded documents",
        { count: documents.length },
        "ROUTER",
      );
      return;
    }
    navigate(APP_ROUTES.checkInNew, { replace: true });
  }, [documents.length, navigate]);

  /** Refs avoid tying unmount cleanup to Redux values (would re-run cleanup in a loop). */
  const docClassFormatListRef = useRef(docClassFormatList);
  const selectedObjectConnectionsRef = useRef(selectedObjectConnections);
  useEffect(() => {
    docClassFormatListRef.current = docClassFormatList;
  }, [docClassFormatList]);
  useEffect(() => {
    selectedObjectConnectionsRef.current = selectedObjectConnections;
  }, [selectedObjectConnections]);

  /** IFS browse-file auto-population bookkeeping (run once per browse file). */
  const processedBulkIFSKeysRef = useRef(new Set());
  /** Per-file guards that re-assert document connections after channel-config settles. */
  const bulkConnectionGuardsRef = useRef(new Map());

  /** Reset shared check-in Redux so class/format/obj-con do not leak into single check-in. */
  const resetSharedCheckinState = useCallback(() => {
    clearBulkCheckinState({
      onDocTitleChange,
      onDocClassFormatChange,
      onFileUploadChange,
      onViewCopyUploadChange,
      onDocReleaseChange,
      onCreateViewCopyDisable,
      onCreateViewCopySelect,
      onObjectConnectionDataLoad,
      onDocClassFormatListChange,
      onRecentObjectConnectionChanged,
      onSelectedObjectConnectionChanged,
      onSelectedKeyLuNameChange,
      onRecentObjectSelectedDescription,
      onRecentObjectDataLoadStatusChanged,
      docClassFormatList: docClassFormatListRef.current,
      selectedObjectConnections: selectedObjectConnectionsRef.current,
    });
  }, [
    onCreateViewCopyDisable,
    onCreateViewCopySelect,
    onDocClassFormatChange,
    onDocClassFormatListChange,
    onDocReleaseChange,
    onDocTitleChange,
    onFileUploadChange,
    onObjectConnectionDataLoad,
    onRecentObjectConnectionChanged,
    onRecentObjectDataLoadStatusChanged,
    onRecentObjectSelectedDescription,
    onSelectedKeyLuNameChange,
    onSelectedObjectConnectionChanged,
    onViewCopyUploadChange,
  ]);

  const resetSharedCheckinStateRef = useRef(resetSharedCheckinState);
  useEffect(() => {
    resetSharedCheckinStateRef.current = resetSharedCheckinState;
  }, [resetSharedCheckinState]);

  useEffect(
    () => () => {
      resetSharedCheckinStateRef.current();
    },
    [],
  );

  useEffect(
    () => () => {
      bulkConnectionGuardsRef.current.forEach((cleanup) => cleanup());
      bulkConnectionGuardsRef.current.clear();
    },
    [],
  );

  /** Retry logical-units / favorites loads after IFS token refresh (same as CheckInNew). */
  useEffect(() => {
    if (!shouldTriggerRefreshTokenPreAction(tokenRefreshPreActionInvoke)) {
      return;
    }

    switch (tokenRefreshPreActionInvoke.actionType) {
      case REFRESH_TOKEN_ACTION.logicalUnitsLoad:
        if (teamsContext) {
          onObjectConnectionsLoad(teamsContext, {
            retry: tokenRefreshPreActionInvoke.retry,
            actionType: REFRESH_TOKEN_ACTION.logicalUnitsLoad,
            refreshResponse: tokenRefreshPreActionInvoke.refreshResponse,
          });
        }
        break;
      case REFRESH_TOKEN_ACTION.recentlyUsedObjectsLoad:
        getRecentObjects({
          retry: tokenRefreshPreActionInvoke.retry,
          actionType: REFRESH_TOKEN_ACTION.recentlyUsedObjectsLoad,
          refreshResponse: tokenRefreshPreActionInvoke.refreshResponse,
        });
        break;
      default:
        break;
    }
  }, [
    getRecentObjects,
    onObjectConnectionsLoad,
    teamsContext,
    tokenRefreshPreActionInvoke,
  ]);

  /** Seed per-file drafts when documents arrive. Title defaults to file name (no ext). */
  useEffect(() => {
    setDraftByKey((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const doc of documents) {
        const id = String(doc.key);
        if (!next[id]) {
          const fileName = doc?.name ?? doc?.title ?? "";
          next[id] = {
            docClass: "",
            docFormat: "",
            docTitle: fileName ? getFileName(fileName) : "",
            overridden: false,
            viewRequired: "N",
            formatRequired: "N",
            objConRequired: "N",
            createViewCopy: false,
            docReleaseStatus: "",
            viewCopyOverridden: false,
            releaseOverridden: false,
            oneDriveDeleteRequired:
              doc?.source === "browse"
                ? false
                : settingsDraftRef.current.oneDriveDeleteRequired === true,
            oneDriveDeleteOverridden: false,
            objectConnections: settingsDraftRef.current.objectConnections ?? [],
            objectConnectionsOverridden: false,
            viewCopyFiles: [],
            uploadViewCopyCount: 0,
          };
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [documents]);

  /**
   * Eagerly auto-populate IFS-named browse files as soon as the documents arrive.
   * Class, format size and object connections are loaded for every browse file on
   * page entry (no expand/collapse required) using the same validation as the
   * single check-in flow. Per-file class-combo spinners are driven by
   * `bulkAutopopulatingKeys`. Redux is left untouched here — the tab-switch
   * hydration effect syncs drafts to the store when a file becomes active.
   * Document connections are re-asserted once the channel-config load settles so
   * they are never overwritten.
   */
  useEffect(() => {
    if (documents.length === 0) {
      return;
    }

    const browseFiles = documents.filter((doc) => {
      const id = String(doc.key);
      const info = getBulkBrowseIFSInfo(doc);
      if (!info) {
        return false;
      }
      if (processedBulkIFSKeysRef.current.has(id)) {
        return false;
      }
      processedBulkIFSKeysRef.current.add(id);
      return true;
    });

    if (browseFiles.length === 0) {
      return;
    }

    let cancelled = false;

    browseFiles.forEach((doc) => {
      const fileKey = String(doc.key);
      const { fileName, parsed } = getBulkBrowseIFSInfo(doc);

      setBulkAutopopulatingKeys((prev) => {
        if (prev.has(fileKey)) return prev;
        const next = new Set(prev);
        next.add(fileKey);
        return next;
      });

      (async () => {
        try {
          const tokenResponse = await new UserAuth().getUserToken(true);
          const accessToken = tokenResponse?.accessToken ?? null;
          if (cancelled || !accessToken) {
            return;
          }

          logger.info("Loading document details for bulk browse file", {
            fileName,
            docNo: parsed.docNo,
            docRev: parsed.docRev,
          }, "CHECKIN");
          const { data, status } = await getDocumentDetails(
            {
              DocClass: parsed.docClass,
              DocNo: parsed.docNo,
              DocSheet: parsed.docSheet,
              DocRev: parsed.docRev,
            },
            accessToken
          );
          if (cancelled) return;
          if (status !== 200) {
            return;
          }

          const { revision } = normalizeDocumentRevisionInfoResponse(data);
          let formatSize = revision?.FormatSize ?? revision?.formatSize ?? "";
          let classRules = await fetchBulkClassRulesWithRetry(
            onGetFormatsByClass,
            parsed.docClass,
            formatSize,
            () => cancelled,
          );

          let connections = extractBulkObjectConnections(revision);
          if (connections.length === 0 || !formatSize) {
            try {
              const newRevResponse = await getDocumentNewRevisionInfo(
                {
                  docClass: parsed.docClass,
                  docNo: parsed.docNo,
                  docSheet: parsed.docSheet,
                  docRev: parsed.docRev,
                  requestDocRevInfo: !formatSize,
                },
                accessToken
              );
              const newRevData = newRevResponse?.data;
              const newRevBasicInfo =
                newRevData?.newRevisionBasicInfo ??
                newRevData?.NewRevisionBasicInfo ??
                {};
              if (!formatSize) {
                const docRevInfo = newRevData?.documentRevision;
                formatSize =
                  docRevInfo?.FormatSize ??
                  docRevInfo?.formatSize ??
                  newRevBasicInfo?.formatSize ??
                  newRevBasicInfo?.FormatSize ??
                  "";
              }
              if (connections.length === 0) {
                const objectConnections =
                  newRevBasicInfo?.objectConnections ??
                  newRevBasicInfo?.ObjectConnections ??
                  [];
                connections = Array.isArray(objectConnections)
                  ? objectConnections.filter((item) => item?.luName && item?.keyRef)
                  : [];
              }
            } catch (err) {
              logger.warn("Failed to load fallback details for bulk browse file", err, "CHECKIN");
            }
          }

          if (cancelled) return;

          if (formatSize && classRules.formats.length === 0) {
            logger.warn("Bulk browse file has format size but no class formats loaded", {
              fileName,
              docClass: parsed.docClass,
              formatSize,
            }, "CHECKIN");
          }
          const hasMatchingFormat =
            formatSize && classRules.formats.some((item) => bulkFormatMatches(item, formatSize));
          const selectedFormat = hasMatchingFormat ? formatSize : "";

          logger.info("Bulk browse autopopulation result", {
            fileName,
            docClass: parsed.docClass,
            formatSize,
            formatsCount: classRules.formats.length,
            formats: classRules.formats.map((f) => f?.FormatValue ?? f?.label ?? f?.Format ?? f),
            hasMatchingFormat,
            selectedFormat,
            connectionsCount: connections.length,
          }, "CHECKIN");

          if (cancelled) return;

          /**
           * Auto-generate view copy for a Word document whose class requires a
           * view copy (same rule as the single check-in flow), so the toggle is
           * checked on without the user having to expand first.
           */
          const autoCreateViewCopy =
            isApiFlagYes(classRules.viewRequired) && isWordLikeFileName(fileName);

          setDraftByKey((prev) => {
            const row = prev[fileKey];
            if (!row) return prev;
            return {
              ...prev,
              [fileKey]: mergeBulkAutopopulatedDraft(row, {
                docClass: parsed.docClass,
                docFormat: selectedFormat,
                viewRequired: classRules.viewRequired,
                formatRequired: classRules.formatRequired,
                objConRequired: classRules.objConRequired,
                objectConnections: connections,
                autoCreateViewCopy,
              }),
            };
          });

          if (connections.length > 0) {
            const previousGuard = bulkConnectionGuardsRef.current.get(fileKey);
            if (previousGuard) {
              previousGuard();
            }
            bulkConnectionGuardsRef.current.set(
              fileKey,
              guardObjectConnectionsAgainstConfigOverwrite(connections, (list) => {
                if (!processedBulkIFSKeysRef.current.has(fileKey)) return;
                setDraftByKey((prev) =>
                  prev[fileKey]
                    ? {
                        ...prev,
                        [fileKey]: {
                          ...prev[fileKey],
                          objectConnections: list,
                          objectConnectionsOverridden: true,
                        },
                      }
                    : prev,
                );
                if (activeKeyRef.current === `file:${fileKey}`) {
                  expectedObjectConnectionsRef.current = list;
                  onSelectedObjectConnectionChanged(list, []);
                }
              }),
            );
          }

          /**
           * If this autopopulated file is the currently active (visible) file,
           * push class/format/connections straight into the shared Redux now.
           * The active-section hydration effect only runs on `activeKey` change,
           * so without this the visible format stays empty until the user
           * collapses and re-expands. Mirrors the single check-in flow, which
           * applies the same values directly. The `overridden` flag on this
           * file's draft prevents the Redux->draft sync from overwriting it.
           */
          if (activeKeyRef.current === `file:${fileKey}`) {
            expectedHydrationRef.current = null;
            expectedObjectConnectionsRef.current = connections;
            onDocClassFormatChange(
              parsed.docClass,
              selectedFormat,
              classRules.viewRequired,
              classRules.formatRequired,
              classRules.objConRequired,
              uploadFiles,
              uploadViewCopyCount,
            );
            if (autoCreateViewCopy) {
              onCreateViewCopySelect(true, { programmatic: true });
            }
            onSelectedObjectConnectionChanged(connections, []);
            if (connections.length > 0) {
              onRecentObjectConnectionChanged(connections);
            }
          }

          logger.info("Bulk browse file auto-populated from IFS name", {
            fileName,
            docClass: parsed.docClass,
            formatSize: selectedFormat || "Not selected",
            objectConnections: connections.length,
          }, "CHECKIN");
        } catch (err) {
          logger.warn("Failed to auto-populate bulk browse file", err, "CHECKIN");
        } finally {
          setBulkAutopopulatingKeys((prev) => {
            if (!prev.has(fileKey)) return prev;
            const next = new Set(prev);
            next.delete(fileKey);
            return next;
          });
        }
      })();
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documents, onGetFormatsByClass]);

  /** Hydrate Redux from the active section's draft (runs whenever `activeKey` changes). */
  useEffect(() => {
    const previousKey = prevActiveKeyForHydrationRef.current;
    if (previousKey?.startsWith("file:") && previousKey !== activeKey) {
      const outgoingFileKey = previousKey.slice(5);
      const outgoingDraft = draftByKeyRef.current[outgoingFileKey] ?? {};
      const mergedOutgoing = mergeFileViewCopyIntoDraft(
        outgoingDraft,
        lastSeenViewCopyRef.current.files,
        lastSeenViewCopyRef.current.count,
      );
      if (
        mergedOutgoing.viewCopyFiles !== outgoingDraft.viewCopyFiles ||
        mergedOutgoing.uploadViewCopyCount !== outgoingDraft.uploadViewCopyCount
      ) {
        const nextDraftMap = {
          ...draftByKeyRef.current,
          [outgoingFileKey]: mergedOutgoing,
        };
        draftByKeyRef.current = nextDraftMap;
        setDraftByKey(nextDraftMap);
      }
    }
    prevActiveKeyForHydrationRef.current = activeKey;

    let targetClass = "";
    let targetFormat = "";
    let targetTitle = "";
    let targetFileName = "";

    if (activeKey === SETTINGS_KEY) {
      const settings = settingsDraftRef.current;
      targetClass = settings.docClass ?? "";
      targetFormat = settings.docFormat ?? "";
      targetTitle = "";
    } else if (activeKey && activeKey.startsWith("file:")) {
      const fileKey = activeKey.slice(5);
      const draft = draftByKeyRef.current[fileKey] ?? {};
      const doc = documentsRef.current.find((d) => String(d.key) === fileKey);
      targetFileName = doc?.name ?? doc?.title ?? "";
      targetClass = draft.docClass ?? "";
      targetFormat = draft.docFormat ?? "";
      targetTitle =
        draft.docTitle ?? (targetFileName ? getFileName(targetFileName) : "");
    } else {
      return;
    }

    const viewReqFlag =
      activeKey === SETTINGS_KEY
        ? (settingsDraftRef.current.viewRequired ?? "N")
        : viewRequired === true || viewRequired === "Y"
          ? "Y"
          : "N";
    const formatReqFlag =
      activeKey === SETTINGS_KEY
        ? (settingsDraftRef.current.formatRequired ?? "N")
        : formatRequired === true || formatRequired === "Y"
          ? "Y"
          : "N";
    const objReqFlag =
      activeKey === SETTINGS_KEY
        ? (settingsDraftRef.current.objConRequired ?? "N")
        : objConRequired === true || objConRequired === "Y"
          ? "Y"
          : "N";

    const uploadPayload = targetFileName ? [{ name: targetFileName }] : [];
    const targetCreateViewCopy =
      activeKey === SETTINGS_KEY
        ? settingsDraftRef.current.createViewCopy === true
        : (() => {
            const fileKey = activeKey.slice(5);
            const draft = draftByKeyRef.current[fileKey] ?? {};
            return draft.viewCopyOverridden
              ? draft.createViewCopy === true
              : settingsDraftRef.current.createViewCopy === true;
          })();
    const targetDocReleaseStatus =
      activeKey === SETTINGS_KEY
        ? (settingsDraftRef.current.docReleaseStatus ?? "")
        : (() => {
            const fileKey = activeKey.slice(5);
            const draft = draftByKeyRef.current[fileKey] ?? {};
            return draft.releaseOverridden
              ? (draft.docReleaseStatus ?? "")
              : (settingsDraftRef.current.docReleaseStatus ?? "");
          })();
    const targetObjectConnections =
      activeKey === SETTINGS_KEY
        ? (settingsDraftRef.current.objectConnections ?? [])
        : (() => {
            const fileKey = activeKey.slice(5);
            const draft = draftByKeyRef.current[fileKey] ?? {};
            return draft.objectConnectionsOverridden
              ? (draft.objectConnections ?? [])
              : (settingsDraftRef.current.objectConnections ?? []);
          })();
    const targetViewCopyFiles =
      activeKey === SETTINGS_KEY
        ? []
        : cloneViewCopyFiles(
            draftByKeyRef.current[activeKey.slice(5)]?.viewCopyFiles ?? [],
          );
    const targetUploadViewCopyCount =
      activeKey === SETTINGS_KEY
        ? 0
        : (draftByKeyRef.current[activeKey.slice(5)]?.uploadViewCopyCount ?? 0);

    expectedHydrationRef.current = {
      docClass: targetClass,
      docFormat: targetFormat,
      docTitle: targetTitle,
      createViewCopy: targetCreateViewCopy,
      docReleaseStatus: targetDocReleaseStatus,
    };
    if (BULK_OC_DEBUG)
      logger.info("[OC_DEBUG] hydration — target connections", {
        activeKey,
        targetKeys: (targetObjectConnections ?? []).map(getObjectConnectionKey),
        source:
          activeKey === SETTINGS_KEY
            ? "settingsDraftRef"
            : draftByKeyRef.current[activeKey.slice(5)]
                  ?.objectConnectionsOverridden
              ? "fileDraft overridden"
              : "settingsDraftRef (inherited)",
      });
    expectedObjectConnectionsRef.current = targetObjectConnections;
    expectedViewCopyHydrationRef.current = {
      files: targetViewCopyFiles,
      count: targetUploadViewCopyCount,
    };

    onDocClassFormatChange(
      targetClass,
      targetFormat,
      viewReqFlag,
      formatReqFlag,
      objReqFlag,
      uploadPayload,
      targetUploadViewCopyCount,
    );
    onDocTitleChange(targetTitle);
    if (targetFileName) {
      onFileUploadChange(
        uploadPayload,
        1,
        targetUploadViewCopyCount,
        viewReqFlag === "Y",
      );
    } else {
      onFileDataChange([], 0);
    }
    onDocReleaseChange(targetDocReleaseStatus === "Released");
    onSelectedObjectConnectionChanged(targetObjectConnections, []);
    const viewRequiredFlag =
      viewReqFlag === "Y" || viewRequired === true || viewRequired === "Y";
    onViewCopyUploadChange(
      targetViewCopyFiles,
      targetUploadViewCopyCount,
      targetUploadViewCopyCount,
      viewRequiredFlag,
      uploadPayload,
    );
    onCreateViewCopySelect(targetCreateViewCopy, { programmatic: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run on `activeKey`; drafts via refs
  }, [activeKey]);

  useEffect(() => {
    const currentObjectConnections = Array.isArray(selectedObjectConnections)
      ? selectedObjectConnections
      : [];
    if (BULK_OC_DEBUG) {
      logger.info("[OC_DEBUG] effect 776 entry", {
        scope: activeKeyRef.current,
        currentKeys: currentObjectConnections.map(getObjectConnectionKey),
        lastSeenKeys: (lastSeenObjectConnectionsRef.current ?? []).map(
          getObjectConnectionKey,
        ),
      });
    }
    if (
      areObjectConnectionsEqual(
        lastSeenObjectConnectionsRef.current,
        currentObjectConnections,
      )
    ) {
      if (BULK_OC_DEBUG)
        logger.info("[OC_DEBUG] effect 776 bail — equal to lastSeen");
      return;
    }

    const expected = expectedObjectConnectionsRef.current;
    if (expected) {
      expectedObjectConnectionsRef.current = null;
      if (areObjectConnectionsEqual(expected, currentObjectConnections)) {
        lastSeenObjectConnectionsRef.current = currentObjectConnections;
        if (BULK_OC_DEBUG)
          logger.info("[OC_DEBUG] effect 776 bail — matched expected");
        return;
      }
    }

    lastSeenObjectConnectionsRef.current = currentObjectConnections;

    const scope = activeKeyRef.current;
    if (!scope) {
      if (BULK_OC_DEBUG) logger.info("[OC_DEBUG] effect 776 bail — no scope");
      return;
    }

    if (scope === SETTINGS_KEY) {
      if (BULK_OC_DEBUG)
        logger.info("[OC_DEBUG] effect 776 updating settingsDraft from Redux", {
          settingKeys: (settingsDraftRef.current.objectConnections ?? []).map(
            getObjectConnectionKey,
          ),
          currentKeys: currentObjectConnections.map(getObjectConnectionKey),
        });
      setSettingsDraft((prev) =>
        areObjectConnectionsEqual(
          prev.objectConnections,
          currentObjectConnections,
        )
          ? prev
          : { ...prev, objectConnections: currentObjectConnections },
      );
      setDraftByKey((prev) => {
        const previousSettingsConnections =
          settingsDraftRef.current.objectConnections ?? [];
        const addedFromSettings = getAddedObjectConnections(
          previousSettingsConnections,
          currentObjectConnections,
        );
        const removedFromSettings = getAddedObjectConnections(
          currentObjectConnections,
          previousSettingsConnections,
        );
        const removedKeys = new Set(
          removedFromSettings.map(getObjectConnectionKey),
        );
        if (BULK_OC_DEBUG)
          logger.info("[OC_DEBUG] effect 776 draftByKey updater", {
            addedKeys: addedFromSettings.map(getObjectConnectionKey),
            removedKeys: [...removedKeys],
            fileIds: Object.keys(prev),
          });
        let changed = false;
        const next = { ...prev };
        for (const id of Object.keys(prev)) {
          const row = prev[id];
          if (row.objectConnectionsOverridden) {
            if (addedFromSettings.length === 0 && removedKeys.size === 0) {
              continue;
            }
            const mergedWithAdded = unionObjectConnections(
              row.objectConnections ?? [],
              addedFromSettings,
            );
            const mergedConnections = removeObjectConnectionsByKeys(
              mergedWithAdded,
              removedKeys,
            );
            if (
              areObjectConnectionsEqual(
                row.objectConnections,
                mergedConnections,
              )
            ) {
              continue;
            }
            changed = true;
            next[id] = {
              ...row,
              objectConnections: mergedConnections,
            };
            continue;
          }
          if (
            !areObjectConnectionsEqual(
              row.objectConnections,
              currentObjectConnections,
            )
          ) {
            changed = true;
            next[id] = {
              ...row,
              objectConnections: currentObjectConnections,
            };
          }
        }
        return changed ? next : prev;
      });
      return;
    }

    if (scope.startsWith("file:")) {
      const fileKey = scope.slice(5);
      setDraftByKey((prev) => {
        const row = prev[fileKey] ?? {};
        const settingsObjectConnections =
          settingsDraftRef.current.objectConnections ?? [];
        const matchesSettings = areObjectConnectionsEqual(
          currentObjectConnections,
          settingsObjectConnections,
        );
        const merged = {
          ...row,
          objectConnections: currentObjectConnections,
          objectConnectionsOverridden: !matchesSettings,
        };
        if (
          areObjectConnectionsEqual(
            row.objectConnections,
            merged.objectConnections,
          ) &&
          row.objectConnectionsOverridden === merged.objectConnectionsOverridden
        ) {
          return prev;
        }
        return { ...prev, [fileKey]: merged };
      });
    }
  }, [selectedObjectConnections]);

  /**
   * Keep file drafts aligned when Settings object connections change, even after
   * navigating through individual file accordions.
   */
  useEffect(() => {
    const currentSettingsConnections = settingsDraft.objectConnections ?? [];
    const previousSettingsConnections =
      previousSettingsObjectConnectionsRef.current ?? [];

    if (BULK_OC_DEBUG)
      logger.info("[OC_DEBUG] effect 893 entry", {
        previousKeys: previousSettingsConnections.map(getObjectConnectionKey),
        currentKeys: currentSettingsConnections.map(getObjectConnectionKey),
      });

    if (
      areObjectConnectionsEqual(
        previousSettingsConnections,
        currentSettingsConnections,
      )
    ) {
      if (BULK_OC_DEBUG)
        logger.info("[OC_DEBUG] effect 893 bail — equal to previous");
      return;
    }

    const addedFromSettings = getAddedObjectConnections(
      previousSettingsConnections,
      currentSettingsConnections,
    );
    const removedFromSettings = getAddedObjectConnections(
      currentSettingsConnections,
      previousSettingsConnections,
    );
    const removedKeys = new Set(
      removedFromSettings.map(getObjectConnectionKey),
    );

    if (BULK_OC_DEBUG)
      logger.info("[OC_DEBUG] effect 893 computed delta", {
        addedKeys: addedFromSettings.map(getObjectConnectionKey),
        removedKeys: [...removedKeys],
      });

    setDraftByKey((prev) => {
      if (BULK_OC_DEBUG)
        logger.info("[OC_DEBUG] effect 893 draftByKey updater", {
          fileIds: Object.keys(prev),
          fileStates: Object.fromEntries(
            Object.entries(prev).map(([id, row]) => [
              id,
              {
                overridden: row.objectConnectionsOverridden,
                keys: (row.objectConnections ?? []).map(getObjectConnectionKey),
              },
            ]),
          ),
        });
      let changed = false;
      const next = { ...prev };

      for (const id of Object.keys(prev)) {
        const row = prev[id];

        if (row.objectConnectionsOverridden) {
          const mergedWithAdded = unionObjectConnections(
            row.objectConnections ?? [],
            addedFromSettings,
          );
          const reconciled = removeObjectConnectionsByKeys(
            mergedWithAdded,
            removedKeys,
          );
          const stillOverridden = !areObjectConnectionsEqual(
            reconciled,
            currentSettingsConnections,
          );

          if (
            areObjectConnectionsEqual(row.objectConnections, reconciled) &&
            row.objectConnectionsOverridden === stillOverridden
          ) {
            continue;
          }

          if (BULK_OC_DEBUG)
            logger.info("[OC_DEBUG] effect 893 overridden file updated", {
              id,
              beforeKeys: (row.objectConnections ?? []).map(
                getObjectConnectionKey,
              ),
              afterKeys: reconciled.map(getObjectConnectionKey),
              beforeOverridden: row.objectConnectionsOverridden,
              afterOverridden: stillOverridden,
            });
          changed = true;
          next[id] = {
            ...row,
            objectConnections: reconciled,
            objectConnectionsOverridden: stillOverridden,
          };
          continue;
        }

        if (
          !areObjectConnectionsEqual(
            row.objectConnections,
            currentSettingsConnections,
          )
        ) {
          if (BULK_OC_DEBUG)
            logger.info("[OC_DEBUG] effect 893 non-overridden file updated", {
              id,
              beforeKeys: (row.objectConnections ?? []).map(
                getObjectConnectionKey,
              ),
              afterKeys: currentSettingsConnections.map(getObjectConnectionKey),
            });
          changed = true;
          next[id] = {
            ...row,
            objectConnections: currentSettingsConnections,
          };
        }
      }

      if (BULK_OC_DEBUG)
        logger.info("[OC_DEBUG] effect 893 draftByKey updater result", {
          changed,
        });
      return changed ? next : prev;
    });

    previousSettingsObjectConnectionsRef.current = currentSettingsConnections;
  }, [settingsDraft.objectConnections]);

  /** Save Redux view-copy uploads into the active file draft only. */
  useEffect(() => {
    const currentFiles = cloneViewCopyFiles(viewCopyFiles);
    const currentCount = uploadViewCopyCount ?? 0;
    if (
      areViewCopyFilesEqual(lastSeenViewCopyRef.current.files, currentFiles) &&
      lastSeenViewCopyRef.current.count === currentCount
    ) {
      return;
    }

    const expected = expectedViewCopyHydrationRef.current;
    if (
      expected &&
      areViewCopyFilesEqual(expected.files, currentFiles) &&
      expected.count === currentCount
    ) {
      lastSeenViewCopyRef.current = {
        files: currentFiles,
        count: currentCount,
      };
      expectedViewCopyHydrationRef.current = null;
      return;
    }

    lastSeenViewCopyRef.current = { files: currentFiles, count: currentCount };

    const scope = activeKeyRef.current;
    if (!scope || !scope.startsWith("file:")) {
      return;
    }

    const fileKey = scope.slice(5);
    setDraftByKey((prev) => {
      const row = prev[fileKey] ?? {};
      const merged = mergeFileViewCopyIntoDraft(
        row,
        currentFiles,
        currentCount,
      );
      if (
        areViewCopyFilesEqual(row.viewCopyFiles, merged.viewCopyFiles) &&
        (row.uploadViewCopyCount ?? 0) === merged.uploadViewCopyCount
      ) {
        return prev;
      }
      return { ...prev, [fileKey]: merged };
    });
  }, [uploadViewCopyCount, viewCopyFiles]);

  /** Save Redux class/format/title back into the active section's draft. */
  useEffect(() => {
    const newClass = docClass ?? "";
    const newFormat = docFormat ?? "";
    const newTitle = typeof docTitle === "string" ? docTitle : "";
    const newCreateViewCopy = createViewCopy === true;
    const newDocReleaseStatus = docReleaseStatus ?? "";

    if (
      lastSeenReduxRef.current.docClass === newClass &&
      lastSeenReduxRef.current.docFormat === newFormat &&
      lastSeenReduxRef.current.docTitle === newTitle &&
      lastSeenReduxRef.current.createViewCopy === newCreateViewCopy &&
      lastSeenReduxRef.current.docReleaseStatus === newDocReleaseStatus
    ) {
      return;
    }

    const expected = expectedHydrationRef.current;
    if (
      expected &&
      expected.docClass === newClass &&
      expected.docFormat === newFormat &&
      expected.docTitle === newTitle &&
      expected.createViewCopy === newCreateViewCopy &&
      expected.docReleaseStatus === newDocReleaseStatus
    ) {
      lastSeenReduxRef.current = {
        docClass: newClass,
        docFormat: newFormat,
        docTitle: newTitle,
        createViewCopy: newCreateViewCopy,
        docReleaseStatus: newDocReleaseStatus,
      };
      expectedHydrationRef.current = null;
      return;
    }

    lastSeenReduxRef.current = {
      docClass: newClass,
      docFormat: newFormat,
      docTitle: newTitle,
      createViewCopy: newCreateViewCopy,
      docReleaseStatus: newDocReleaseStatus,
    };

    const scope = activeKeyRef.current;
    if (!scope) return;

    if (scope === SETTINGS_KEY) {
      const viewReq = viewRequired === true || viewRequired === "Y" ? "Y" : "N";
      const formatReq =
        formatRequired === true || formatRequired === "Y" ? "Y" : "N";
      const objReq =
        objConRequired === true || objConRequired === "Y" ? "Y" : "N";
      setSettingsDraft((prev) =>
        prev.docClass === newClass &&
        prev.docFormat === newFormat &&
        prev.viewRequired === viewReq &&
        prev.formatRequired === formatReq &&
        prev.objConRequired === objReq &&
        prev.createViewCopy === (createViewCopy === true) &&
        prev.docReleaseStatus === (docReleaseStatus ?? "")
          ? prev
          : {
              docClass: newClass,
              docFormat: newFormat,
              viewRequired: viewReq,
              formatRequired: formatReq,
              objConRequired: objReq,
              createViewCopy: createViewCopy === true,
              docReleaseStatus: docReleaseStatus ?? "",
              oneDriveDeleteRequired: prev.oneDriveDeleteRequired,
              objectConnections: prev.objectConnections ?? [],
            },
      );
      setDraftByKey((prev) => {
        const settingsOneDriveDelete =
          settingsDraftRef.current.oneDriveDeleteRequired === true;
        const settingsCreateViewCopy = createViewCopy === true;
        let changed = false;
        const next = { ...prev };
        for (const id of Object.keys(prev)) {
          const row = prev[id];
          if (row.overridden) {
            continue;
          }
          const keepViewCopyOverride =
            draftHasManualViewCopyAttachment(row) ||
            (row.uploadViewCopyCount ?? 0) > 0;
          if (row.docClass !== newClass || row.docFormat !== newFormat) {
            changed = true;
            next[id] = {
              ...row,
              docClass: newClass,
              docFormat: newFormat,
              overridden: false,
              viewRequired: viewReq,
              formatRequired: formatReq,
              objConRequired: objReq,
              createViewCopy: settingsCreateViewCopy,
              viewCopyOverridden: keepViewCopyOverride ? true : false,
              ...(row.releaseOverridden
                ? {}
                : { docReleaseStatus: docReleaseStatus ?? "" }),
              ...(row.oneDriveDeleteOverridden
                ? {}
                : { oneDriveDeleteRequired: settingsOneDriveDelete }),
            };
          } else if (row.createViewCopy !== settingsCreateViewCopy) {
            changed = true;
            next[id] = {
              ...row,
              createViewCopy: settingsCreateViewCopy,
              viewCopyOverridden: keepViewCopyOverride ? true : false,
            };
          } else if (
            !row.releaseOverridden &&
            row.docReleaseStatus !== (docReleaseStatus ?? "")
          ) {
            changed = true;
            next[id] = {
              ...next[id],
              docReleaseStatus: docReleaseStatus ?? "",
            };
          } else if (
            !row.oneDriveDeleteOverridden &&
            row.oneDriveDeleteRequired !== settingsOneDriveDelete
          ) {
            changed = true;
            next[id] = {
              ...next[id],
              oneDriveDeleteRequired: settingsOneDriveDelete,
            };
          } else if (
            !row.overridden &&
            (row.viewRequired !== viewReq ||
              row.formatRequired !== formatReq ||
              row.objConRequired !== objReq)
          ) {
            changed = true;
            next[id] = {
              ...row,
              viewRequired: viewReq,
              formatRequired: formatReq,
              objConRequired: objReq,
            };
          }
        }
        return changed ? next : prev;
      });
      return;
    }

    if (scope.startsWith("file:")) {
      const fileKey = scope.slice(5);
      const viewReq = viewRequired === true || viewRequired === "Y" ? "Y" : "N";
      const formatReq =
        formatRequired === true || formatRequired === "Y" ? "Y" : "N";
      const objReq =
        objConRequired === true || objConRequired === "Y" ? "Y" : "N";
      setDraftByKey((prev) => {
        const row = prev[fileKey] ?? {};
        const settingsOneDriveDelete =
          settingsDraftRef.current.oneDriveDeleteRequired === true;
        const merged = {
          ...row,
          docClass: newClass,
          docFormat: newFormat,
          docTitle: newTitle || row.docTitle || "",
          overridden: true,
          viewRequired: viewReq,
          formatRequired: formatReq,
          objConRequired: objReq,
          createViewCopy: createViewCopy === true,
          docReleaseStatus: docReleaseStatus ?? "",
          oneDriveDeleteRequired: row.oneDriveDeleteOverridden
            ? row.oneDriveDeleteRequired === true
            : settingsOneDriveDelete,
          oneDriveDeleteOverridden: row.oneDriveDeleteOverridden === true,
          viewCopyOverridden: true,
          releaseOverridden: true,
        };
        if (
          row.docClass === merged.docClass &&
          row.docFormat === merged.docFormat &&
          row.docTitle === merged.docTitle &&
          row.overridden === true &&
          row.viewRequired === merged.viewRequired &&
          row.formatRequired === merged.formatRequired &&
          row.objConRequired === merged.objConRequired &&
          row.createViewCopy === merged.createViewCopy &&
          row.docReleaseStatus === merged.docReleaseStatus &&
          row.oneDriveDeleteRequired === merged.oneDriveDeleteRequired &&
          row.oneDriveDeleteOverridden === merged.oneDriveDeleteOverridden &&
          row.viewCopyOverridden === true &&
          row.releaseOverridden === true
        ) {
          return prev;
        }
        return { ...prev, [fileKey]: merged };
      });
    }
  }, [
    createViewCopy,
    docClass,
    docFormat,
    docReleaseStatus,
    docTitle,
    formatRequired,
    objConRequired,
    viewRequired,
  ]);

  const fromRoute = state.from ?? APP_ROUTES.checkInNew;

  const finalizeBulkCheckinSuccess = useCallback(() => {
    onAllowSelectionChange(false);
    onCheckinDocumentProgress(DOCUMENT_LOAD_STATUS.onStart);
    onOneDriveDataLoad(DATA_LOAD_STATUS.onLoad);
    resetSharedCheckinState();
    navigate(APP_ROUTES.allDocuments);
  }, [
    navigate,
    onAllowSelectionChange,
    onCheckinDocumentProgress,
    onOneDriveDataLoad,
    resetSharedCheckinState,
  ]);

  const showCheckinProgressDialog =
    checkinBulkLoading ||
    isCheckinInLoadingState(docCheckinType, checkinDocumentProgress);

  const accordionSettingsOuter = useMemo(
    () => ({
      mb: "16px",
      borderRadius: "8px",
      border: `1px solid ${theme.colors.addovation20Green ?? "#b7d9c8"}`,
      boxShadow: "none !important",
      "&:before": { display: "none" },
      backgroundColor: BULK_ACC_CARD_SURFACE_BG,
    }),
    [theme.colors.addovation20Green],
  );

  const toggleRow = useCallback((key, open) => {
    const fileKey = `file:${String(key)}`;
    if (open) {
      const id = String(key);
      const settings = settingsDraftRef.current;
      setDraftByKey((prev) => {
        const row = prev[id];
        if (!row || row.overridden) {
          return prev;
        }
        const settingsOneDriveDelete = settings.oneDriveDeleteRequired === true;
        const classFormatInSync =
          row.docClass === settings.docClass &&
          row.docFormat === settings.docFormat;
        const rulesInSync =
          row.viewRequired === (settings.viewRequired ?? "N") &&
          row.formatRequired === (settings.formatRequired ?? "N") &&
          row.objConRequired === (settings.objConRequired ?? "N");
        const oneDriveInSync =
          row.oneDriveDeleteOverridden ||
          row.oneDriveDeleteRequired === settingsOneDriveDelete;
        const objectConnectionsInSync =
          row.objectConnectionsOverridden ||
          areObjectConnectionsEqual(
            row.objectConnections,
            settings.objectConnections ?? [],
          );
        if (
          classFormatInSync &&
          rulesInSync &&
          oneDriveInSync &&
          objectConnectionsInSync
        ) {
          return prev;
        }
        return {
          ...prev,
          [id]: {
            ...row,
            docClass: settings.docClass ?? "",
            docFormat: settings.docFormat ?? "",
            viewRequired: settings.viewRequired ?? "N",
            formatRequired: settings.formatRequired ?? "N",
            objConRequired: settings.objConRequired ?? "N",
            ...(row.objectConnectionsOverridden
              ? {}
              : { objectConnections: settings.objectConnections ?? [] }),
            ...(row.oneDriveDeleteOverridden
              ? {}
              : { oneDriveDeleteRequired: settingsOneDriveDelete }),
          },
        };
      });
    }
    if (open) {
      setSettingsPanelExpanded(false);
    }
    setActiveKey((prev) => {
      if (open) return fileKey;
      if (prev === fileKey) return INACTIVE_KEY;
      return prev;
    });
  }, []);

  const toggleSettings = useCallback((open) => {
    setSettingsPanelExpanded(open);
    if (open) {
      setActiveKey(SETTINGS_KEY);
    }
  }, []);

  const handleSettingsOneDriveDeleteChange = useCallback((event) => {
    const checked = !!event?.target?.checked;
    setSettingsDraft((prev) =>
      prev.oneDriveDeleteRequired === checked
        ? prev
        : { ...prev, oneDriveDeleteRequired: checked },
    );
    setDraftByKey((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const doc of documentsRef.current) {
        const id = String(doc.key);
        const row = prev[id];
        if (!row || row.oneDriveDeleteOverridden) {
          continue;
        }
        if (row.oneDriveDeleteRequired !== checked) {
          changed = true;
          next[id] = { ...row, oneDriveDeleteRequired: checked };
        }
      }
      return changed ? next : prev;
    });
  }, []);

  const handleFileOneDriveDeleteChange = useCallback(
    (fileKey) => (event) => {
      const checked = !!event?.target?.checked;
      setDraftByKey((prev) => {
        const row = prev[fileKey] ?? {};
        if (
          row.oneDriveDeleteRequired === checked &&
          row.oneDriveDeleteOverridden === true
        ) {
          return prev;
        }
        return {
          ...prev,
          [fileKey]: {
            ...row,
            oneDriveDeleteRequired: checked,
            oneDriveDeleteOverridden: true,
          },
        };
      });
    },
    [],
  );

  /** Settings-level rules from get-docformatinfo (class + view copy + object connections). */
  const areSharedBulkRequirementsMet = useMemo(() => {
    if (
      !settingsDraft.docClass ||
      String(settingsDraft.docClass).trim() === ""
    ) {
      return false;
    }
    if (
      isApiFlagYes(settingsDraft.formatRequired) &&
      (!settingsDraft.docFormat ||
        String(settingsDraft.docFormat).trim() === "")
    ) {
      return false;
    }
    if (
      isApiFlagYes(settingsDraft.viewRequired) &&
      settingsDraft.createViewCopy !== true
    ) {
      return false;
    }
    if (
      isApiFlagYes(settingsDraft.objConRequired) &&
      (!Array.isArray(settingsDraft.objectConnections) ||
        settingsDraft.objectConnections.length === 0)
    ) {
      return false;
    }
    return true;
  }, [settingsDraft]);

  const isFileDraftIncomplete = useCallback(
    (draft, doc) => {
      const fileName = doc?.name ?? doc?.title ?? doc?.key ?? "Document";

      if (!draft) {
        //console.log(`[incomplete] ${fileName}: true — no draft`);
        return true;
      }

      const effectiveClass = draft.overridden
        ? draft.docClass
        : draft.docClass || settingsDraft.docClass;
      const effectiveFormat = draft.overridden
        ? draft.docFormat
        : draft.docFormat || settingsDraft.docFormat;
      const formatReqForFile = draft.overridden
        ? isApiFlagYes(draft.formatRequired)
        : isApiFlagYes(settingsDraft.formatRequired);
      const viewReqForFile = resolveBulkViewRequiredFlag(draft, settingsDraft);
      const objReqForFile = draft.overridden
        ? isApiFlagYes(draft.objConRequired)
        : isApiFlagYes(settingsDraft.objConRequired);
      const effectiveObjectConnections = draft.objectConnectionsOverridden
        ? draft.objectConnections
        : settingsDraft.objectConnections;
      const effectiveCreateViewCopy = draft.viewCopyOverridden
        ? draft.createViewCopy === true
        : settingsDraft.createViewCopy === true;
      const effectiveUploadViewCopyCount =
        getEffectiveViewCopyUploadCount(draft);

      if (!effectiveClass || String(effectiveClass).trim() === "") {
        //console.log(`[incomplete] ${fileName}: true — missing document class`);
        return true;
      }
      if (
        formatReqForFile &&
        (!effectiveFormat || String(effectiveFormat).trim() === "")
      ) {
        //console.log(
        //  `[incomplete] ${fileName}: true — format required but missing`,
        //);
        return true;
      }
      if (
        viewReqForFile &&
        !isViewCopyRequirementMet({
          viewRequired: true,
          uploadViewCopyCount: effectiveUploadViewCopyCount,
          createViewCopy: effectiveCreateViewCopy,
          fileName,
        })
      ) {
        //console.log(
        //  `[incomplete] ${fileName}: true — view copy required but not satisfied`,
        //);
        return true;
      }
      if (
        objReqForFile &&
        (!Array.isArray(effectiveObjectConnections) ||
          effectiveObjectConnections.length === 0)
      ) {
        //console.log(
        //  `[incomplete] ${fileName}: true — object connection required but none selected`,
        //);
        return true;
      }
      if (!draft.docTitle || String(draft.docTitle).trim() === "") {
        //console.log(`[incomplete] ${fileName}: true — missing document title`);
        return true;
      }

      //console.log(`[incomplete] ${fileName}: false — all requirements met`);
      return false;
    },
    [settingsDraft],
  );

  const isBulkFileRowIncomplete = useCallback(
    (draft, doc) => isFileDraftIncomplete(draft, doc),
    [isFileDraftIncomplete],
  );

  const draftsForValidation = useMemo(
    () =>
      mergeActiveFileViewCopyIntoDraftMap(
        draftByKey,
        activeKey,
        viewCopyFiles,
        uploadViewCopyCount,
      ),
    [activeKey, draftByKey, uploadViewCopyCount, viewCopyFiles],
  );

  const allFilesReady = useMemo(() => {
    if (documents.length === 0) {
      return false;
    }
    return documents.every(
      (d) => !isBulkFileRowIncomplete(draftsForValidation[String(d.key)], d),
    );
  }, [documents, draftsForValidation, isBulkFileRowIncomplete]);

  const isCheckInButtonDisabled = useMemo(() => {
    if (
      checkinBulkLoading ||
      isCheckinInLoadingState(docCheckinType, checkinDocumentProgress)
    ) {
      return true;
    }
    if (!allFilesReady) return true;
    if (objectConnectionDataLoad !== DATA_LOAD_STATUS.completed) return true;
    return false;
  }, [
    allFilesReady,
    checkinBulkLoading,
    checkinDocumentProgress,
    docCheckinType,
    objectConnectionDataLoad,
  ]);

  const footerCancelButtonTextStyles = useMemo(
    () => ({
      fontFamily: theme.fonts?.regular,
      fontSize: `${theme.fontSizes?.normal ?? 12}px`,
    }),
    [theme.fonts?.regular, theme.fontSizes?.normal],
  );

  const footerCancelFluentStyles = useMemo(
    () => ({
      root: { marginRight: "10px", ...footerCancelButtonTextStyles },
    }),
    [footerCancelButtonTextStyles],
  );

  const handleFooterCancel = useCallback(() => {
    resetSharedCheckinState();
    navigate(fromRoute);
  }, [fromRoute, navigate, resetSharedCheckinState]);

  const handleFooterCheckIn = useCallback(async () => {
    const incompleteFiles = documents.filter((doc) =>
      isBulkFileRowIncomplete(draftsForValidation[String(doc.key)], doc),
    );
    if (incompleteFiles.length > 0) {
      dispatch({
        type: ON_ERROR_MESSAGE_INVOKE,
        payload: {
          type: ERROR_TYPE,
          message: `Complete all required fields for every file (${incompleteFiles.length} incomplete).`,
        },
      });
      return;
    }
    if (objectConnectionDataLoad !== DATA_LOAD_STATUS.completed) {
      dispatch({
        type: ON_ERROR_MESSAGE_INVOKE,
        payload: {
          type: ERROR_TYPE,
          message:
            "Object connections are still loading. Try again in a moment.",
        },
      });
      return;
    }
    onDocCheckinTypeChange(DOC_CHECK_IN_TYPE.checkinNew);
    onCheckinDocumentProgress(DOCUMENT_LOAD_STATUS.onLoad);
    onCheckinDocumentProgress(DOCUMENT_LOAD_STATUS.onLoading);

    const draftsForSubmit = mergeActiveFileViewCopyIntoDraftMap(
      draftByKeyRef.current,
      activeKeyRef.current,
      viewCopyFiles,
      uploadViewCopyCount,
    );
    if (draftsForSubmit !== draftByKeyRef.current) {
      draftByKeyRef.current = draftsForSubmit;
      setDraftByKey(draftsForSubmit);
    }

    const inlineViewCopyCount = documents.filter((doc) =>
      draftHasManualViewCopyAttachment(draftsForSubmit[String(doc.key)]),
    ).length;

    const docInfos = [];
    for (const doc of documents) {
      const draft = draftsForSubmit[String(doc.key)];
      const isBrowseSource = doc?.source === "browse";
      const hasBrowseFile =
        isBrowseSource &&
        typeof Blob !== "undefined" &&
        doc?.file instanceof Blob;
      const hasBrowseBase64 =
        isBrowseSource &&
        typeof doc?.base64str === "string" &&
        doc.base64str.trim() !== "";
      const fileUrl = getBulkOneDriveFileUrlFromDoc(doc);
      const oneDriveServerContext =
        fileUrl || hasBrowseBase64 || hasBrowseFile
          ? null
          : buildBulkOneDriveServerContext(doc, teamsContext);
      if (!fileUrl && !oneDriveServerContext && !hasBrowseBase64 && !hasBrowseFile) {
        onCheckinDocumentProgress(DOCUMENT_LOAD_STATUS.onStart);
        dispatch({
          type: ON_ERROR_MESSAGE_INVOKE,
          payload: {
            type: ERROR_TYPE,
            message: `Could not prepare OneDrive context for "${doc.name ?? doc.title ?? "document"}". Try again.`,
          },
        });
        return;
      }
      docInfos.push(
        buildBulkDocInfo({
          doc,
          draft,
          fileUrl,
          oneDriveServerContext,
          formatRequired: draft?.overridden
            ? (draft?.formatRequired ?? formatRequired)
            : (settingsDraft.formatRequired ?? formatRequired),
          viewRequired: draft?.overridden
            ? (draft?.viewRequired ?? viewRequired)
            : (settingsDraft.viewRequired ?? viewRequired),
          createViewCopy: draft?.viewCopyOverridden
            ? draft?.createViewCopy === true
            : settingsDraft.createViewCopy === true,
          docReleaseStatus: draft?.releaseOverridden
            ? (draft?.docReleaseStatus ?? "")
            : (settingsDraft.docReleaseStatus ?? ""),
          selectedObjectConnections: draft?.objectConnectionsOverridden
            ? (draft?.objectConnections ?? [])
            : (settingsDraft.objectConnections ?? []),
          uploadViewCopyCount: draft?.uploadViewCopyCount ?? 0,
        }),
      );
    }

    logger.info(
      "[CheckInNewBulkContent] bulk check-in submit",
      {
        count: docInfos.length,
        inlineViewCopyAttachments: inlineViewCopyCount,
      },
      "DIALOG",
    );

    const action = await dispatch(
      checkinDocumentsBulkThunk({
        documents: docInfos,
        __toast: { suppressToast: true },
      }),
    );

    if (!checkinDocumentsBulkThunk.fulfilled.match(action)) {
      onCheckinDocumentProgress(DOCUMENT_LOAD_STATUS.onStart);
      dispatch({
        type: ON_ERROR_MESSAGE_INVOKE,
        payload: {
          type: ERROR_TYPE,
          title: "Bulk check-in failed",
          message:
            action?.payload?.message ||
            "Unable to check in one or more documents.",
        },
      });
      return;
    }

    const bulk = action.payload?.data ?? {};
    const successCount = bulk.successCount ?? 0;
    const failureCount = bulk.failureCount ?? 0;

    if (successCount === 0) {
      onCheckinDocumentProgress(DOCUMENT_LOAD_STATUS.onStart);
      const firstError =
        bulk.results?.[0]?.errorMessage ??
        "Bulk check-in failed for all documents.";
      dispatch({
        type: ON_ERROR_MESSAGE_INVOKE,
        payload: {
          type: ERROR_TYPE,
          title: "Bulk check-in failed",
          message: `${failureCount} failed. ${firstError}`,
        },
      });
      return;
    }

    const {
      groupId: deleteGroupId,
      channelId,
      fileRoot: deleteFileRoot,
    } = resolveGroupId(teamsContext);
    const deleteMembershipType =
      teamsContext?.channel?.membershipType || "Regular";
    const oneDriveDeleteIds = collectBulkOneDriveDeleteIds(
      documents,
      draftsForSubmit,
      settingsDraft,
      bulk,
      docInfos,
    );

    if (oneDriveDeleteIds.length > 0) {
      try {
        await deleteOneDriveDocumentsBulk(
          deleteMembershipType,
          deleteGroupId || "",
          channelId ?? "",
          teamsContext?.user?.tenant?.id ?? "",
          deleteFileRoot,
          oneDriveDeleteIds,
        );
      } catch (_e) {
        // Deletion failure should not block the check-in success flow.
      }
    }

    const metadataUpdates = collectBulkMetadataUpdates(
      documents,
      draftsForSubmit,
      settingsDraft,
      bulk,
      docInfos,
    );
    if (metadataUpdates.length > 0) {
      try {
        const metaTokenResponse = await new UserAuth().getUserToken(true);
        const metaAccessToken = metaTokenResponse?.accessToken ?? null;
        if (metaAccessToken) {
          const metaWebUser =
            teamsContext?.user?.userPrincipalName ||
            teamsContext?.user?.displayName ||
            "";
          const lastActionDate = new Date().toISOString().split("T")[0];
          for (const update of metadataUpdates) {
            try {
              await updateOneDriveMetadata(metaAccessToken, {
                userId: teamsContext?.user?.id ?? "",
                tenantId: teamsContext?.user?.tenant?.id ?? "",
                teamId: deleteGroupId || "",
                channelId: teamsContext?.channel?.id ?? "",
                oneDriveRoot: deleteFileRoot ?? "",
                documentId: update.documentId,
                webUser: metaWebUser,
                metadata: {
                  docClass: update.docClass,
                  docNo: update.docNo,
                  docRev: update.docRev,
                  docSheet: update.docSheet,
                  formatSize: update.docFormat ?? "",
                  state: update.state,
                  fileState: "CheckedIn",
                  owner: metaWebUser,
                  lastActionDate,
                  isDownloaded: false,
                },
              });
            } catch (_metaErr) {
              logger.warn(
                "bulk updateOneDriveMetadata failed for one document, continuing",
                _metaErr,
                "CHECKIN",
              );
            }
          }
        }
      } catch (_tokenErr) {}
    }

    dispatch({
      type: ON_SUCCESS_MESSAGE_INVOKE,
      payload: {
        type: SUCCESS_TYPE,
        title: getBulkCheckinToastTitle(failureCount),
        message: formatBulkCheckinToastMessage(successCount, failureCount),
      },
    });

    finalizeBulkCheckinSuccess();
  }, [
    deleteOneDriveDocumentsBulk,
    dispatch,
    documents,
    draftByKey,
    draftsForValidation,
    finalizeBulkCheckinSuccess,
    formatRequired,
    isBulkFileRowIncomplete,
    objConRequired,
    objectConnectionDataLoad,
    onCheckinDocumentProgress,
    onDocCheckinTypeChange,
    settingsDraft,
    teamsContext,
    uploadViewCopyCount,
    viewCopyFiles,
    viewRequired,
  ]);

  if (!documents.length) {
    return null;
  }

  return (
    <Stack
      styles={{
        root: {
          width: "100%",
          overflow: "visible",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {showCheckinProgressDialog && (
        <ReusableDialog
          isOpen
          onClose={() => {}}
          showHeader={false}
          isBlocking
          centerContent
          dialogMinHeight={100}
          dialogMinWidth="300px"
          bodyMinHeight={60}
        >
          <MessageBoxContent
            isLoading
            loadingText={CHECKIN_LOADING_MESSAGE}
            showOkButton={false}
          />
        </ReusableDialog>
      )}
      <Stack.Item disableShrink>
        <Accordion
          elevation={0}
          expanded={activeKey === SETTINGS_KEY && settingsPanelExpanded}
          onChange={(_e, expanded) => toggleSettings(Boolean(expanded))}
          sx={accordionSettingsOuter}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: "#4c8d6a" }} />}
            sx={{
              ...BULK_ACC_HEADER_SUMMARY_SX,
              px: 2,
              "& .MuiAccordionSummary-content": {
                ...BULK_ACC_HEADER_SUMMARY_SX["& .MuiAccordionSummary-content"],
                gap: "8px",
              },
            }}
          >
            <Stack
              horizontal
              verticalAlign="center"
              tokens={{ childrenGap: 8 }}
            >
              <TaskListSquareSettingsRegular
                aria-hidden
                primaryFill="#237b4b"
                style={{ width: 22, height: 22, flexShrink: 0 }}
              />
              <Typography
                sx={{
                  flex: 1,
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  color: "#2d6e4f",
                  fontFamily: theme.fonts.regular,
                  fontWeight: 600,
                  fontSize: `${theme.fontSizes?.normal ?? 12}px`,
                }}
              >
                Settings
              </Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails
            sx={{
              px: 2,
              pb: 2,
              pt: 0,
              backgroundColor: theme.colors.backgroundColor,
              display: "flex",
              flexDirection: "column",
              gap: "16px",
            }}
          >
            {activeKey === SETTINGS_KEY && (
              <Box
                sx={{ display: "flex", flexDirection: "column", width: "100%" }}
              >
                <DocumentBasicItem
                  key="settings"
                  omitTitleSection
                  omitOneDriveDeletionRow
                  bulkSettingsViewCopy
                  onTreeDataLoadingChange={setIsTreeDataLoading}
                  objectConnectionTabKey={objectConnectionTabKey}
                  onObjectConnectionTabKeyChange={setObjectConnectionTabKey}
                  tabKey={tabKey}
                />
                {!isBrowseOnly && (
                  <DeleteOneDriveCheckbox
                    checked={settingsDraft.oneDriveDeleteRequired === true}
                    onChange={handleSettingsOneDriveDeleteChange}
                  />
                )}
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      </Stack.Item>

      <Stack.Item
        disableShrink
        styles={{ root: { marginTop: 20, width: "100%" } }}
      >
        <Text
          styles={{
            root: {
              fontWeight: 600,
              fontFamily: theme.fonts.regular,
              fontSize: theme.fontSizes?.normal ?? 12,
              color: theme.colors.fontColor,
            },
          }}
        >
          File(s)
        </Text>
      </Stack.Item>

      <Stack.Item disableShrink styles={{ root: { width: "100%" } }}>
        <Box sx={{ pb: "24px", width: "100%" }}>
          {documents.map((doc) => {
            const id = String(doc.key);
            const draft = draftsForValidation[id] ?? draftByKey[id];
            return (
              <BulkCheckInFileRow
                key={id}
                doc={doc}
                expanded={activeKey === `file:${id}`}
                incomplete={isBulkFileRowIncomplete(draft, doc)}
                isAutopopulating={bulkAutopopulatingKeys.has(id)}
                onToggleExpand={toggleRow}
                onTreeDataLoadingChange={setIsTreeDataLoading}
                objectConnectionTabKey={objectConnectionTabKey}
                onObjectConnectionTabKeyChange={setObjectConnectionTabKey}
                tabKey={tabKey}
                oneDriveDeleteChecked={resolveOneDriveDeleteRequired(
                  draft,
                  settingsDraft,
                )}
                onOneDriveDeleteChange={handleFileOneDriveDeleteChange(id)}
                onRemove={handleRemoveDocument}
              />
            );
          })}
        </Box>
      </Stack.Item>

      <Stack.Item disableShrink styles={{ root: { width: "100%" } }}>
        <Stack
          horizontal
          horizontalAlign="center"
          verticalAlign="center"
          tokens={{ childrenGap: 12 }}
          styles={{
            root: {
              width: "100%",
              paddingTop: 24,
              paddingBottom: 16,
              flexWrap: "wrap",
              justifyContent: "center",
            },
          }}
        >
          <div style={{ marginRight: "10px" }}>
            <PrimaryButton
              styles={footerCancelFluentStyles}
              style={styleTheme.CancelButtonFillStyle}
              id="btncancel-bulk"
              text="Cancel"
              ariaLabel="Cancel and return to list"
              onClick={handleFooterCancel}
            />
          </div>
          <PrimaryButton
            style={
              isCheckInButtonDisabled
                ? styleTheme.DisableButtoneStyle
                : styleTheme.PrimaryButtonFillStyle
            }
            text={showCheckinProgressDialog ? "Checking in…" : "Check-in"}
            ariaLabel="Submit bulk check-in"
            disabled={isCheckInButtonDisabled}
            onClick={handleFooterCheckIn}
          />
        </Stack>
      </Stack.Item>
    </Stack>
  );
}
